import { t, Trans } from "@lingui/macro";
import { Button, Dialog, DialogContent, DialogTitle, DialogTrigger } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useLayoutEffect, useState } from "react";
import { Link } from "react-router";

import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { useAuthStore } from "@/client/stores/auth";

const NAV_LINK_IDS = ["product", "tools", "pricing", "about", "help", "changelog"] as const;

type NavLinkId = (typeof NAV_LINK_IDS)[number];

const NAV_HREF: Record<NavLinkId, string> = {
  product: "/",
  tools: "/tools",
  pricing: "/pricing",
  about: "/about",
  help: "/help",
  changelog: "/changelog",
};

type NavBarProps = {
  className?: string;
};

const NavLinkLabel = ({ id }: { id: NavLinkId }) => {
  if (id === "product") return <Trans>Product</Trans>;
  if (id === "tools") return <Trans>Free Tools</Trans>;
  if (id === "pricing") return <Trans>Pricing</Trans>;
  if (id === "about") return <Trans>About</Trans>;
  if (id === "help") return <Trans>Help</Trans>;
  return <Trans>Changelog</Trans>;
};

export const NavBar = ({ className }: NavBarProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isLoggedIn = useAuthStore((state) => Boolean(state.user));

  // The pre-paint `data-auth-hint` flag only masks the logged-out SSG snapshot
  // (public/scripts/initialize-auth-hint.js). Once React owns the header it
  // renders the real state, so drop the flag — otherwise a session that ended
  // client-side would keep its Sign in / Start free buttons hidden.
  useLayoutEffect(() => {
    delete document.documentElement.dataset.authHint;
  }, []);

  const closeMobile = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-40 border-b border-border/80 bg-background/75 backdrop-blur",
        className,
      )}
    >
      <div className="mx-auto flex h-14 max-w-[1280px] items-center justify-between px-4 sm:px-6">
        {/* Left: Wordmark + Desktop Nav */}
        <div className="flex items-center gap-8">
          <Link aria-label={t`Apply Edge`} to="/">
            <Logo withText size={28} />
          </Link>

          <nav className="hidden items-center gap-5 text-[13px] text-muted-foreground md:flex">
            {NAV_LINK_IDS.map((id) => (
              <Link key={id} className="transition hover:text-foreground" to={NAV_HREF[id]}>
                <NavLinkLabel id={id} />
              </Link>
            ))}
          </nav>
        </div>

        {/* Right: Theme toggle + Auth buttons */}
        <div className="flex items-center gap-2">
          {/* Single source of truth — same component the home header and
              auth layout use. Per Speckit Constitution IX (UI Component
              Reuse), every theme toggle in the app must come from
              <ThemeSwitch /> so the icon, animation, and behaviour stay
              consistent. */}
          <ThemeSwitch />

          {isLoggedIn ? (
            /* Logged-in: single Dashboard CTA */
            <Link
              className="rounded-full bg-primary px-3.5 py-1.5 text-[12.5px] font-medium text-primary-foreground hover:opacity-90"
              to="/dashboard"
            >
              <Trans>Dashboard</Trans>
            </Link>
          ) : (
            <>
              {/* Sign in (desktop) */}
              <Link
                data-auth-guest
                className="hidden rounded-full border border-border px-3.5 py-1.5 text-[12.5px] font-medium text-foreground hover:bg-muted md:inline-flex"
                to="/auth/login"
              >
                <Trans>Sign in</Trans>
              </Link>

              {/* Start free (desktop) */}
              <Link
                data-auth-guest
                className="inline-flex h-10 items-center rounded-full bg-primary px-4 text-[13px] font-medium text-primary-foreground hover:opacity-90 sm:h-auto sm:px-3.5 sm:py-1.5 sm:text-[12.5px]"
                to="/auth/register"
              >
                <Trans>Start free</Trans>
              </Link>
            </>
          )}

          {/* Mobile menu trigger */}
          <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <DialogTrigger asChild>
              <Button
                aria-label={t`Open navigation menu`}
                className="-mr-2 grid size-11 place-items-center rounded-lg sm:mr-0 sm:size-auto md:hidden"
                id="mobile-menu-trigger"
                size="icon"
                variant="ghost"
              >
                <span className="flex flex-col gap-1.5">
                  <span className="block h-px w-5 bg-current" />
                  <span className="block h-px w-5 bg-current" />
                  <span className="block h-px w-3 bg-current" />
                </span>
              </Button>
            </DialogTrigger>

            <DialogContent
              className="top-0 translate-y-0 sm:max-w-sm"
              onCloseAutoFocus={(e) => {
                // Return focus to trigger after close
                e.preventDefault();
                document.querySelector<HTMLElement>("#mobile-menu-trigger")?.focus();
              }}
            >
              <DialogTitle className="sr-only">
                <Trans>Navigation menu</Trans>
              </DialogTitle>

              <nav className="flex flex-col gap-4 pt-4">
                {NAV_LINK_IDS.map((id) => (
                  <Link
                    key={id}
                    className="text-[15px] font-medium text-foreground hover:text-muted-foreground"
                    to={NAV_HREF[id]}
                    onClick={closeMobile}
                  >
                    <NavLinkLabel id={id} />
                  </Link>
                ))}

                <div className="mt-2 flex flex-col gap-2">
                  {isLoggedIn ? (
                    <Link
                      className="rounded-full bg-primary px-4 py-2 text-center text-[13px] font-medium text-primary-foreground hover:opacity-90"
                      to="/dashboard"
                      onClick={closeMobile}
                    >
                      <Trans>Dashboard</Trans>
                    </Link>
                  ) : (
                    <>
                      <Link
                        className="rounded-full border border-border px-4 py-2 text-center text-[13px] font-medium text-foreground hover:bg-muted"
                        to="/auth/login"
                        onClick={closeMobile}
                      >
                        <Trans>Sign in</Trans>
                      </Link>
                      <Link
                        className="rounded-full bg-primary px-4 py-2 text-center text-[13px] font-medium text-primary-foreground hover:opacity-90"
                        to="/auth/register"
                        onClick={closeMobile}
                      >
                        <Trans>Start free</Trans>
                      </Link>
                    </>
                  )}
                </div>
              </nav>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </header>
  );
};
